import { useTranslation } from 'react-i18next';
import type { ModalProps } from '@/components/ModalRenderer/components/types';
import { Modal } from '@/components/ModalRenderer/Modal';
import { formatInterestRate } from '@/localization/percentages';

const today = new Date().toLocaleDateString('sv-SE', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

type Payload = {
  product: {
    creditLimit: number;
    interestRate: number;
    effectiveInterestRate: number;
    setupFee: number;
    installmentFee: number;
    withdrawalFee: number;
    installmentAmount: number;
    totalCost: number;
    totalRepaid: number;
  };
  userData: {
    email: string;
    nationalNumber: string;
  };
};
type Props = ModalProps<Payload>;

function CreditAgreement({ payload: { userData, product } }: Props) {
  const { t } = useTranslation('Common');

  return (
    <Modal title="Kreditavtal Fairlo Varda">
      <div className="[&_h2,_h3,_b]:text-body-semibold [&_p,_h2,_h3]:mb-4">
        <p>Förmedlad av Fairlo AB (Fairlo)</p>

        <p>{today}</p>

        <h2>1. Allmän information</h2>

        <p>
          Kreditgivare är Bankaktiebolaget Nordiska (publ), organisationsnummer 556760-6032, Box 173, 101 23
          Stockholm.
        </p>

        <p>Kreditförmedlare är Fairlo AB, organisationsnummer 559084-1101, Box 111 10, 100 61 Stockholm.</p>

        <p>
          Kreditgivaren har givit Fairlo AB i uppdrag att administrera krediten för Kreditgivarens räkning,
          varför Kredittagaren fortsatt kan vända sig till Fairlo AB med eventuella frågor till dess
          Kreditgivaren meddelar annorlunda.
        </p>

        <p>
          Detta är Kredittagarens kreditavtal för Fairlo Varda. Avtalet har ingåtts mellan Kredittagaren och
          Kreditgivaren. Nedan framgår de specifika egenskaperna och villkoren för Kredittagarens kredit. För
          övriga villkor och bestämmelser gäller Fairlos Allmänna villkor samt den Betalningsplan som vid
          varje enskild tidpunkt gäller för krediten och som framgår av Kredittagarens Mina sidor på
          www.fairlo.se. Vid signering av detta Kreditavtal godkänner Kredittagaren i) att Fairlos Allmänna
          villkor är en del av Kreditavtalet och att Kredittagaren tagit del av och godkänt dessa och ii) att
          den Betalningsplan som vid varje enskild tidpunkt godkänts av Kredittagaren med BankID via
          www.fairlo.se är en del av Kreditavtalet.
        </p>

        <p>
          Observera att du som Kredittagare inte betalar något enligt detta kreditavtal förrän du väljer att
          aktivt utnyttja ditt Kreditutrymme. När du väljer att göra ett uttag kommer du att behöva
          identifiera dig med BankID för att krediten ska betalas ut.
        </p>

        <h2>2. Parter</h2>

        <b>Kredittagare</b>
        <p data-private className="mb-0!">
          {userData.email}
        </p>
        <p>
          {/* Add dash before the 4th digit from the last */}
          Personnummer: <span data-private>{userData.nationalNumber.replace(/(\d{4})$/, '-$1')}</span>
        </p>

        <b>Kreditgivare</b>
        <p className="mb-0!">Bankaktiebolaget Nordiska (publ)</p>
        <p className="mb-0!">Box 173</p>
        <p className="mb-0!">101 23 STOCKHOLM</p>
        <p>Organisationsnummer: 556760-6032</p>

        <h2>3. Krediten</h2>

        <b>Typ av kredit</b>
        <p>Kontokredit utan säkerhet.</p>
        <p>
          Kredittagaren ges rätt att nyttja en kredit inom ramen för beviljat Kreditutrymme. Vad som avses med
          Kreditutrymme framgår av de Allmänna villkoren.
        </p>

        <b>Nyttjande</b>
        <p>Kredit utbetalas till det bankkonto som Kredittagaren har angivit och anslutit.</p>

        <b>Återbetalning</b>
        <p>Månadsbelopp i enlighet med en annuitetsplan.</p>

        <b>Kreditutrymme</b>
        <p>{t('money', { amount: product.creditLimit })}</p>

        <b>Ränta och avgifter</b>
        <p>
          Ränta utgår med en nominell årlig räntesats om {formatInterestRate(product.interestRate)} från den
          tidpunkt Krediten utbetalas.
        </p>
        <p>
          Vid utbetalningen av Krediten tillkommer en uppläggningsavgift:{' '}
          {t('money', { amount: product.setupFee })} månadsavgift (varje faktura):{' '}
          {t('money', { amount: product.installmentFee })} och uttagsavgift:{' '}
          {t('money', { amount: product.withdrawalFee })}
        </p>

        <b>Månadsbelopp</b>
        <p>
          {t('money', { amount: product.installmentAmount })} per månad. Hur mycket Kredittagaren ska betala
          varje månad framgår av den senast avtalade Betalningsplanen i enlighet med Fairlos Allmänna villkor
        </p>

        <b>Löptid</b>
        <p>Tills vidare</p>

        <b>Total Kostnad</b>
        <p>
          Den totala kostnaden för Krediten uppgår till {t('money', { amount: product.totalCost })} om
          Kredittagaren betalar i enlighet med Betalningsplanen som gäller när krediten utbetalas.
          Kredittagarens totala kostnad för krediten beror dels på den omfattning Kredittagaren valt att
          utnyttja Krediten och dels på Kredittagarens återbetalningstakt.
        </p>

        <b>Effektiv ränta</b>
        <p>{formatInterestRate(product.effectiveInterestRate)}</p>

        <b>Totalt belopp</b>
        <p>
          Det sammanlagda belopp Kredittagaren kommer att betala uppgår till{' '}
          {t('money', { amount: product.totalRepaid })} om Kredittagaren betalar i enlighet med
          Betalningsplanen som gäller vid fullt utnyttjat kreditbelopp när krediten utbetalas.
        </p>

        <b>Ändring av ränta och avgifter</b>
        <p>
          Kreditgivaren har rätt att ändra priserna i prisbilagan i enlighet med vad som framgår av de
          Allmänna villkoren. Kreditgivaren äger när som helst under kreditperioden rätt att besluta om
          höjning av särskild avgift i den mån Kreditgivarens kostnader ökat för den åtgärd avgiften avser att
          täcka. Kreditgivaren tillämpar ovanstående villkor om ändring av avgift även till Kredittagarens
          förmån och sänker således avgiften i den mån Kreditgivarens kostnader för den åtgärd avgiften avser
          att täcka minskat.
        </p>

        <h2>4. Signering</h2>
        <p>
          Detta avtal har upprättats elektroniskt och signerats med personligt BankID av Kredittagaren.
          Kredittagaren har mottagit en kopia av detta kreditavtal och de Allmänna villkoren via e-post till
          den e-postadress som Kredittagaren angivit i sin ansökan om kredit.
        </p>
      </div>
    </Modal>
  );
}

export default CreditAgreement;
